import type { GenerateDocumentRef } from './featheryClient/integrationClient';
import Field from './entities/Field';
import { featheryWindow } from './browser';
import { getAllElements } from './formHelperFunctions';
import { changeStep } from './stepHelperFunctions';
import { FieldOptions, formatAllFormFields } from './fieldHelperFunctions';
import {
  defaultClient,
  FieldValues,
  getFieldValues,
  initState,
  setFieldValues,
  updateTheme,
  updateUserId
} from './init';
import internalState, {
  AlloyEntities,
  GetConfigParams,
  CreateEgnyteFolderParams,
  GetDocusignEnvelopeParams,
  UploadFileToEgnyteParams,
  LoanProCustomerObject,
  RunComputerAgentOptions,
  SendDocusignParams,
  UpdateDocusignEnvelopeParams,
  setFormInternalState
} from './internalState';
import type { FormRuntimeEvent } from './internalState';
import { validateElements } from './validation';
import { getPanelRuntimeSnapshot } from './panelRuntime';
import { linkRequestHeaders } from './accessLink';
import { fillFields, FillFieldInput } from './formActions/fillFields';
import { liveDocumentAccessor } from '../robin/document/mounts';
import { navigateToStep } from './formActions/navigateToStep';
import { clickElement } from './formActions/clickElement';
import { runLogicRule, RunLogicRuleParams } from './formActions/runLogicRule';
import {
  addTableRow,
  deleteTableRow,
  focusTableCell,
  getTableIssues,
  getTableRows,
  setTableCells,
  SetTableCellsInput,
  setTableFindings,
  SetTableFindingsInput,
  TableRowTarget,
  triggerTableAction
} from './formActions/tables';
import { STEP_EVENT_SKIP } from './stepEvents';
import { AnswersPendingError } from './offlineRequestHandler';
import {
  FillQuikParams,
  ForwardInboxEmailOptions,
  HubActionOptions,
  IntegrationActionIds,
  IntegrationActionOptions,
  PageSelectionInput
} from '@feathery/client-utils';

const uploadFieldIdByKey = (steps: any, fieldKey: string): string | null => {
  for (const step of Object.values(steps ?? {}) as any[]) {
    const match = (step?.servar_fields ?? []).find(
      ({ servar }: any) =>
        servar.key === fieldKey && servar.type === 'file_upload'
    );
    if (match) return match.servar.id;
  }
  return null;
};

/** Whether any step of the form holds a document editor container, the current step or not. */
const formHasDocumentElement = (steps: any): boolean =>
  (Object.values(steps ?? {}) as any[]).some((step) =>
    (step?.subgrids ?? []).some(
      (container: any) => !!container?.properties?.document_editor
    )
  );

/**
 * Used by contextRef in <Form />, renderAt for vanillajs, and the lifecycle
 * methods
 *
 * @param formUuid
 * @returns Form context object
 */
export const getFormContext = (formUuid: string) => {
  // If internal state for the formUuid doesn't exist, initialize it so that
  // there will be object stability for the for state object props.  Important for
  // use of certain props in the callback functions.
  let formState = internalState[formUuid];
  if (!formState) {
    formState = { fields: {} } as any;
    setFormInternalState(formUuid, formState);
  }

  return {
    userId: initState.userId,
    sdkKey: initState.sdkKey,
    formName: formState.formName,
    formId: formState.formId,
    _getInternalUserId: () => initState._internalUserId,
    fields: formState.fields,
    products: formState.products,
    cart: formState.cart,
    collaborator: formState.collaborator,
    setFormCompletion: () => {
      const { client, currentStep } = formState;
      return client
        .registerEvent({
          step_key: currentStep.key,
          next_step_key: '',
          event: STEP_EVENT_SKIP,
          completed: true
        })
        .catch((error: unknown) => {
          // Completion is refused while answers are still being saved. Tell
          // the user, and reject so the calling logic does not carry on as if
          // the form were complete.
          if (error instanceof AnswersPendingError)
            featheryWindow().alert(error.message);
          throw error;
        });
    },
    setProgress: (val: any) => formState.setUserProgress(val),
    openDataMapping: ({ hubs }: { hubs: any[] }) =>
      formState.setDataMappingState?.({ show: true, hubs }),
    updateUserId,
    updateTheme,
    goToStep: (stepKey: any) => {
      const { currentStep, navigate, steps, setStepKey, client, trackHashes } =
        formState;
      const changed = changeStep(
        stepKey,
        currentStep.key,
        steps,
        setStepKey,
        navigate,
        client,
        trackHashes
      );
      if (changed) formState.latestStepName = stepKey;
    },
    isTestForm: () => initState.isTestEnv,
    // What the person sees right now: current step, every field's value, hidden fields
    snapshot: () => getPanelRuntimeSnapshot(formUuid, { masked: true }),
    // What a host sends with its own backend requests to act as this submission, read when the request is made
    requestIdentity: (): {
      fuserKey?: string;
      headers: Record<string, string>;
    } => ({
      fuserKey: initState.userId,
      headers: {
        ...linkRequestHeaders(initState.linkToken, initState.linkSecret),
        ...(initState.collaboratorId
          ? { 'X-Feathery-Collaborator': initState.collaboratorId }
          : {})
      }
    }),
    subscribe: (listener: (event: FormRuntimeEvent) => void) => {
      const listeners = (formState.runtimeListeners ??= new Set());
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    // Acting for the person on the current step, with the same checks as their own edits and clicks
    fillFields: (fields: FillFieldInput[]) => fillFields(formUuid, fields),
    // Robin's document editing: the mounted document's descriptor for the request context, one bridge
    // payload in and its response out, and the end of the editing turn when Robin's turn settles
    liveDocument: liveDocumentAccessor(formUuid, {
      hasDocumentElement: () =>
        formHasDocumentElement(internalState[formUuid]?.steps)
    }),
    // Moves through the step's own navigation like the person's click, unlike goToStep
    navigateToStep: (stepKey: string) => navigateToStep(formUuid, stepKey),
    clickElement: (elementId: string, repeatIndex?: number) =>
      clickElement(formUuid, elementId, repeatIndex),
    // Runs one of the designer's tool rules for the person and reports what it changed
    runLogicRule: (ruleId: string, params?: RunLogicRuleParams) =>
      runLogicRule(formUuid, ruleId, params),
    // Reads and edits a table on the current step through its own grid, where the person's edits land
    getTableRows: (
      tableId: string,
      query?: { offset?: number; limit?: number; search?: string }
    ) => getTableRows(formUuid, tableId, query),
    setTableCells: (tableId: string, cells: SetTableCellsInput[]) =>
      setTableCells(formUuid, tableId, cells),
    addTableRow: (tableId: string) => addTableRow(formUuid, tableId),
    deleteTableRow: (tableId: string, target: TableRowTarget) =>
      deleteTableRow(formUuid, tableId, target),
    triggerTableAction: (
      tableId: string,
      target: TableRowTarget,
      actionLabel?: string
    ) => triggerTableAction(formUuid, tableId, target, actionLabel),
    // Marks, focuses and reads the cells of a spreadsheet table the person is looking at
    setTableFindings: (tableId: string, findings: SetTableFindingsInput[]) =>
      setTableFindings(formUuid, tableId, findings),
    focusTableCell: (
      tableId: string,
      target: TableRowTarget & { fieldKey?: string }
    ) => focusTableCell(formUuid, tableId, target),
    getTableIssues: (
      tableId: string,
      query: TableRowTarget & { scope: 'table' | 'row' }
    ) => getTableIssues(formUuid, tableId, query),
    isLastStep: () => {
      const step = formState.currentStep;
      return step.next_conditions.length === 0;
    },
    getStepProperties: () => {
      const state = formState;
      const step = state?.currentStep ?? {};
      const rootStyles = step
        ? step.subgrids.find((grid: any) => grid.position.length === 0).styles
        : {};

      const containerIdByPosition: Record<string, string> = {};
      step.subgrids.forEach((grid: any) => {
        containerIdByPosition[grid.position.join(',')] = grid.id;
      });

      const fieldKeysByContainer: Record<string, string[]> = {};

      // add field key to every position prefix
      step.servar_fields?.forEach((field: any) => {
        const pos = field.position;
        for (let i = pos.length - 1; i > 0; i--) {
          const posPrefix = pos.slice(0, i).join(',');
          const containerId = containerIdByPosition[posPrefix];
          if (containerId) {
            if (!fieldKeysByContainer[containerId]) {
              fieldKeysByContainer[containerId] = [];
            }
            fieldKeysByContainer[containerId].push(field.servar.key);
          }
        }
      });

      const hideIfMap: Record<string, any> = {};
      getAllElements(step).forEach(([el, type]) => {
        if (!el.hide_ifs.length) return;
        const id = type === 'field' ? el.servar.key : el.id;
        hideIfMap[id] = {
          elementType: type,
          name: type === 'field' ? el.servar.key : el.key ?? el.id,
          showOrHide: el.show_logic ? 'show' : 'hide',
          childFields:
            type === 'subgrid' ? fieldKeysByContainer[el.id] : undefined,
          rules: el.hide_ifs.map((hideIf: any) => ({
            comparisonField: hideIf.field_key,
            comparator: hideIf.comparison,
            comparisonValues: hideIf.values
          }))
        };
      });
      return {
        totalSteps: Object.keys(state.steps).length,
        stepName: step.key ?? '',
        previousStepName: state.previousStepName,
        backgroundColor: rootStyles?.background_color ?? 'FFFFFF',
        language: state.language,
        hideRules: hideIfMap
      };
    },
    validateStep: (showErrors = true) => {
      const {
        currentStep,
        visiblePositions,
        formRef,
        formSettings,
        getErrorCallback,
        setInlineErrors
      } = formState;

      const { errors } = validateElements({
        step: currentStep,
        visiblePositions,
        triggerErrors: showErrors,
        errorType: formSettings.errorType,
        formRef,
        errorCallback: getErrorCallback(),
        setInlineErrors
      });
      return errors;
    },
    openUrl: async (url: string, target = '_blank') => {
      if (target !== '_blank') {
        const formClient = formState.client;
        await Promise.all([
          defaultClient.flushCustomFields(),
          formClient.flushCustomFields(),
          formClient.submitQueue,
          formClient.eventQueue
        ]);
        // submitQueue may have updated when awaiting the first time
        await formClient.submitQueue;
      }
      featheryWindow()?.open(url, target, 'noopener');
    },
    runIntegrationActions: (
      actionIds: IntegrationActionIds,
      options: IntegrationActionOptions
    ) => formState.client.customRolloutAction(actionIds, options),
    runComputerAgent: async (
      agentId: string,
      options?: RunComputerAgentOptions
    ) => {
      const res = await formState.runComputerAgent(agentId, options);
      return res.ok ? res.payload : { status: 'error', message: res.error };
    },
    runAIExtraction: async (
      extractionId: string,
      options = { waitForCompletion: false },
      pages?: PageSelectionInput
    ) => formState.runAIExtraction(extractionId, options, pages),
    forwardInboxEmail: (options: ForwardInboxEmailOptions) =>
      formState.forwardInboxEmail(options),
    setCalendlyUrl: (url: string) => formState.setCalendlyUrl(url),
    fillQuikForms: (params: FillQuikParams) => formState.fillQuikForms(params),
    sendDocusignEnvelope: (params: SendDocusignParams) =>
      formState.sendDocusignEnvelope(params),
    getDocusignEnvelope: (params: GetDocusignEnvelopeParams) =>
      formState.getDocusignEnvelope(params),
    updateDocusignEnvelope: (params: UpdateDocusignEnvelopeParams) =>
      formState.updateDocusignEnvelope(params),
    uploadFileToEgnyte: (params: UploadFileToEgnyteParams) =>
      formState.uploadFileToEgnyte(params),
    createEgnyteFolder: (params: CreateEgnyteFolderParams) =>
      formState.createEgnyteFolder(params),
    getDocusignBrands: () => formState.getDocusignBrands(),
    applyAlloyJourney: (journeyToken: string, entities: AlloyEntities) =>
      formState.client.alloyJourneyApplication(journeyToken, entities),
    searchLoanProCustomer: () =>
      formState.client.searchLoanProCustomerByAuthorizedEmail(),
    createLoanProCustomer: (bodyParams: LoanProCustomerObject) =>
      formState.client.createLoanProCustomerWithAuthorizedEmail(bodyParams),
    setCollaboratorAsCompleted: (templateId: string) =>
      formState.client.setCollaboratorAsCompleted(templateId),
    setTaskStatus: (templateId: string, taskStatusId: string) =>
      formState.client.setTaskStatus(templateId, taskStatusId),
    dataHubAction: ({
      hubId,
      operation,
      entryId,
      data,
      where,
      verification
    }: HubActionOptions) =>
      formState.client.dataHubAction({
        hubId,
        operation,
        entryId,
        data,
        where,
        verification
      }),
    generateDocuments: ({
      documentIds: documentSources,
      signers,
      envelopeAction,
      signMethod,
      draft,
      toolbarActions,
      closeOnAction,
      emailSubject,
      emailBlurb,
      customFields,
      repeatable,
      download,
      merge,
      mergedFileName,
      zipName,
      saveDocumentFieldKey,
      redirect
    }: {
      // A plain template UUID string, a file upload field itself (e.g.
      // `FileUpload1`), or a source object such as the Quik item
      // `{ kind: 'quik' }` — mirroring the action config.
      documentIds: (GenerateDocumentRef | Field)[];
      // Per-document, per-role signer emails; a document with no entry here
      // gets no signer. `roleId` targets one of the document's signer roles,
      // or is left off to cover every role of that document. `filler` marks
      // whoever signs inline in the form rather than being emailed a link —
      // only their signing token comes back.
      // `phone` challenges that recipient by SMS before the envelope opens
      // (DocuSign only). Give it in E.164 form, e.g. '+15555555555'; a number
      // with no country code is taken as +1. Omit it to send without a
      // challenge.
      // `repeatIndex` targets a zero-based PDF copy when repeatable=true.
      // It requires roleId. Omit it to apply that role to every copy; a
      // copy-specific entry overrides the default for the same role.
      signers?: {
        documentId: string;
        roleId?: string;
        repeatIndex?: number;
        // DocuSign-only identity across documents; omit for email grouping.
        signerId?: string;
        email: string;
        name?: string;
        phone?: string;
        filler?: boolean;
      }[];
      envelopeAction?: 'sign' | 'fill' | 'download' | 'save' | 'open_in_editor';
      signMethod?: 'feathery' | 'docusign';
      // Create a DocuSign draft without opening the editor or emailing signers.
      // Requires signMethod 'docusign'; envelopeAction defaults to 'sign'.
      draft?: boolean;
      // Only for envelopeAction 'open_in_editor': which buttons the editor
      // toolbar offers. 'draft' is DocuSign-only (it finalizes as a sign with
      // draft=true).
      toolbarActions?: ('sign' | 'download' | 'save' | 'draft')[];
      // Only for envelopeAction 'open_in_editor': false keeps the editor open
      // after a toolbar action, and the flow continues once the filler closes
      // it. Defaults to true.
      closeOnAction?: boolean;
      // Only for signMethod 'docusign': the subject and body of the envelope's
      // signing email, as sendDocusignEnvelope takes them.
      emailSubject?: string;
      emailBlurb?: string;
      // DocuSign envelope metadata (not PDF fields), e.g. account_submission_id.
      customFields?: Record<
        string,
        string | { listItems: string[]; value?: string }
      >;
      repeatable?: boolean;
      download?: boolean;
      merge?: boolean;
      mergedFileName?: string;
      zipName?: string;
      saveDocumentFieldKey?: string;
      // Where the signing page sends the filler when they finish.
      redirect?: boolean | string;
    }) => {
      if (customFields !== undefined && signMethod !== 'docusign') {
        return Promise.reject(
          new Error(
            "generateDocuments: customFields require signMethod 'docusign'"
          )
        );
      }
      // Capture before pending field saves or document review can yield.
      customFields =
        customFields === undefined
          ? undefined
          : Object.fromEntries(
              Object.entries(customFields).map(([name, value]) => [
                name,
                typeof value === 'string'
                  ? value
                  : { ...value, listItems: [...value.listItems] }
              ])
            );
      signers = signers?.map((signer) => ({ ...signer }));
      if (
        signMethod !== 'docusign' &&
        (signers?.some((signer) => signer.signerId?.trim()) ||
          documentSources.some(
            (doc) =>
              !(doc instanceof Field) &&
              typeof doc !== 'string' &&
              doc.kind === 'quik' &&
              doc.forms?.some(
                (form) => Object.keys(form.signerIds ?? {}).length
              )
          ))
      ) {
        return Promise.reject(
          new Error(
            "generateDocuments: signer IDs require signMethod 'docusign'"
          )
        );
      }
      if (draft) {
        if (
          signMethod !== 'docusign' ||
          (envelopeAction && envelopeAction !== 'sign') ||
          download ||
          toolbarActions?.length
        ) {
          return Promise.reject(
            new Error(
              "generateDocuments: draft requires signMethod 'docusign' and " +
                "envelopeAction 'sign' (or omitted), without download or toolbarActions"
            )
          );
        }
        if (!formState.generateEnvelopeFlow) {
          return Promise.reject(
            new Error('generateDocuments: draft requires a mounted <Form />')
          );
        }
        envelopeAction = 'sign';
      }
      // A field passed as-is is named by key, resolved to its id below.
      let documentIds: GenerateDocumentRef[] = documentSources.map((doc) =>
        doc instanceof Field ? { kind: 'file_upload', field_key: doc.id } : doc
      );
      const usesRichOptions =
        !!signMethod ||
        !!signers?.length ||
        !!envelopeAction ||
        !!toolbarActions?.length ||
        !!repeatable ||
        documentIds.some((doc) => typeof doc !== 'string');
      // Capture explicit Quik inputs before awaiting saves or opening review.
      // Caller edits to the original object must not change this request.
      documentIds = documentIds.map((doc) =>
        typeof doc !== 'string' && doc.kind === 'quik' && doc.forms
          ? {
              ...doc,
              forms: doc.forms.map((form) => ({
                ...form,
                fields: { ...form.fields },
                ...(form.signerIds ? { signerIds: { ...form.signerIds } } : {})
              }))
            }
          : typeof doc !== 'string' && doc.kind === 'template'
          ? {
              ...doc,
              ...(doc.references ? { references: [...doc.references] } : {})
            }
          : doc
      );
      const hasReferences = documentIds.some(
        (doc) =>
          typeof doc !== 'string' &&
          ((doc.kind === 'template' && doc.references !== undefined) ||
            (doc.kind === 'quik' &&
              doc.forms?.some((form) => form.reference !== undefined)))
      );
      if (hasReferences && (signMethod !== 'docusign' || merge)) {
        return Promise.reject(
          new Error(
            "generateDocuments: references require signMethod 'docusign' and cannot be merged"
          )
        );
      }
      const unknownUploadKeys: string[] = [];
      let missingUploadField = false;
      documentIds = documentIds.map((doc) => {
        if (typeof doc === 'string' || doc.kind !== 'file_upload') return doc;
        // Headless forms have no steps; the mounted-<Form /> error below applies.
        if (!formState.generateEnvelopeFlow) return doc;
        if (doc.field_id) return doc;
        if (!doc.field_key) {
          missingUploadField = true;
          return doc;
        }
        const fieldId = uploadFieldIdByKey(formState.steps, doc.field_key);
        if (!fieldId) unknownUploadKeys.push(doc.field_key);
        return { kind: 'file_upload', field_id: fieldId ?? '' };
      });
      if (missingUploadField) {
        return Promise.reject(
          new Error(
            'generateDocuments: a file upload source needs its field, e.g. ' +
              'documentIds: [templateId, FileUpload1]'
          )
        );
      }
      if (unknownUploadKeys.length) {
        return Promise.reject(
          new Error(
            'generateDocuments: no file upload field with key ' +
              unknownUploadKeys.join(', ')
          )
        );
      }
      // Document generation reads whatever the fuser has on file, so pending
      // field edits have to land before it runs or it fills/signs stale data.
      const flushFields = () =>
        Promise.all([
          formState.client.flushCustomFields(),
          defaultClient.flushCustomFields()
        ]);
      // Source objects, DocuSign, signers, the editor, and the sign/save
      // envelope actions all need the same endpoint + editor flow the Generate
      // Documents action uses, so route through the <Form />-registered flow
      // when any are requested. Otherwise keep the simple, backward-compatible
      // client path (template fill / merge / download).
      if (formState.generateEnvelopeFlow && usesRichOptions) {
        return flushFields().then(() =>
          formState.generateEnvelopeFlow!({
            type: 'open_fuser_envelopes',
            documents: documentIds,
            // The flow has no separate download flag; it's an envelope action,
            // unless the call already asks to sign.
            envelope_action:
              envelopeAction ??
              (download &&
              !signMethod &&
              !signers?.length &&
              !toolbarActions?.length &&
              !redirect
                ? 'download'
                : undefined),
            sign_method: signMethod,
            ...(draft !== undefined ? { draft } : {}),
            // Omitted rather than nulled: the backend's role_id rejects an
            // explicit null, and leaving it off spreads the email across
            // every role of that document. A phone is omitted the same way,
            // since a present one is the request to challenge that recipient.
            envelope_signers: signers?.map(
              ({
                documentId,
                roleId,
                repeatIndex,
                signerId,
                email,
                name,
                phone,
                filler
              }) => ({
                document_id: documentId,
                ...(roleId ? { role_id: roleId } : {}),
                ...(repeatIndex !== undefined
                  ? { repeat_index: repeatIndex }
                  : {}),
                email,
                ...(signerId?.trim() ? { signer_id: signerId.trim() } : {}),
                ...(name ? { name } : {}),
                ...(phone ? { phone } : {}),
                filler: !!filler
              })
            ),
            editor_toolbar_actions: toolbarActions,
            ...(closeOnAction !== undefined
              ? { editor_close_on_action: closeOnAction }
              : {}),
            email_subject: emailSubject,
            email_blurb: emailBlurb,
            custom_fields: customFields,
            repeatable,
            merge_docs: merge,
            merged_file_name: mergedFileName,
            envelope_zip_name: zipName,
            save_document_field_key: saveDocumentFieldKey,
            redirect,
            run_async: true
          })
        );
      }
      // Reached only when no <Form /> flow is registered (headless/vanillajs).
      // The simple client path interpolates documentIds straight into its poll
      // URL, so a source object would stringify to "[object Object]" and poll
      // against a key the backend never wrote — until it timed out. Per-role
      // signers have no home on that path either, and dropping them silently
      // would generate an envelope nobody is ever asked to sign. Say so.
      if (documentIds.some((doc) => typeof doc !== 'string')) {
        return Promise.reject(
          new Error(
            'generateDocuments: document source objects (e.g. the Quik item) ' +
              'require a mounted <Form />; pass template ids only here.'
          )
        );
      }
      if (signers?.length) {
        return Promise.reject(
          new Error(
            'generateDocuments: per-role signers require a mounted <Form />.'
          )
        );
      }
      return flushFields().then(() =>
        formState.client.generateDocuments({
          documentIds: documentIds as string[],
          download,
          merge,
          repeatable,
          mergedFileName,
          zipName
        })
      );
    },
    getQuikForms: (props: { dealerNames: string[] }) =>
      formState.client.getQuikForms(props),
    getQuikFormRoles: (props: { formIds: number[] }) =>
      formState.client.getQuikFormRoles(props),
    getQuikAccountForms: (props: {
      custodian: string;
      accountType: string;
      isTransition?: boolean;
    }) => formState.client.getQuikAccountForms(props),
    getConfig: (params: GetConfigParams) => formState.client.getConfig(params),
    // deprecated
    setFieldValues: (userVals: FieldValues): void => {
      console.warn(
        'setFieldValues is deprecated.  Please use the fields object and set the value directly in individual fields instead.'
      );
      return setFieldValues(userVals);
    },
    // deprecated
    getFieldValues: () => {
      console.warn(
        'getFieldValues is deprecated.  Please use the fields object instead.'
      );
      return getFieldValues();
    },
    // @deprecated
    // TODO: remove when support for setFieldOptions is dropped
    setFieldOptions: (newOptions: FieldOptions) => {
      console.warn(
        'setFieldOptions is deprecated.  Please use the fields object instead and set the options directly on individual fields.'
      );
      return formState.updateFieldOptions(newOptions);
    },
    // @deprecated
    // TODO: remove when support for getFormFields is dropped
    getFormFields: () => {
      console.warn(
        'getFormFields is deprecated.  Please use the fields object instead.'
      );
      return formatAllFormFields(formState.steps, true);
    },
    // @deprecated
    // TODO: remove when support for setFieldErrors is dropped
    setFieldErrors: (
      errors: Record<string, string | { index: number; message: string }>
    ) => {
      console.warn(
        'setFieldErrors is deprecated.  Please use the fields object instead and set the error directly on a field.'
      );
      return formState.setFieldErrors(errors);
    }
  };
};
